import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { UserModule } from '@/store/modules/user'
import { format } from 'date-fns'

import utils from './axios-dependencies/utils'
import normalizeHeaderName from './axios-dependencies/normalizeHeaderName'
import { toasts } from '@/utils/toasts'
import i18n from '@/lang'

export const HTTP_UNAUTHORIZED = 403

function setContentTypeIfUnset(headers: any, value: string) {
  if (
    !utils.isUndefined(headers) &&
    utils.isUndefined(headers['Content-Type'])
  ) {
    headers['Content-Type'] = value
  }
}

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  timeout: 15000
  // withCredentials: true // send cookies when cross-domain requests
})

// Request interceptors
service.interceptors.request.use(
  (config: AxiosRequestConfig<any>) => {
    // Add X-Access-Token header to every request, you can add other custom headers here
    if (UserModule.token) {
      config.headers = config.headers || {}
      config.headers['X-Access-Token'] = UserModule.token
    }

    config.transformRequest = function (data, headers) {
      normalizeHeaderName(headers, 'Accept')
      normalizeHeaderName(headers, 'Content-Type')

      if (
        utils.isFormData(data) ||
        utils.isArrayBuffer(data) ||
        utils.isBuffer(data) ||
        utils.isStream(data) ||
        utils.isFile(data) ||
        utils.isBlob(data)
      ) {
        return data
      }
      if (utils.isArrayBufferView(data)) {
        return data.buffer
      }
      if (utils.isURLSearchParams(data)) {
        setContentTypeIfUnset(
          headers,
          'application/x-www-form-urlencoded;charset=utf-8'
        )
        return data.toString()
      }
      if (utils.isObject(data)) {
        setContentTypeIfUnset(headers, 'application/json;charset=utf-8')
        return JSON.stringify(data, function (k, v) {
          if (this[k] instanceof Date) {
            return format(this[k], 'yyyy-MM-dd HH:mm:ss')
          }
          return v
        })
      }
      return data
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptors
service.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response
  },
  (error) => {
    const errorRequest = error.request as AxiosResponse | undefined
    if (errorRequest) {
      switch (errorRequest.status) {
        case HTTP_UNAUTHORIZED:
          toasts.error(i18n.t('common.texts.unauthMessage') as string)
          break
      }
    }
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error)
  }
)

export default service
